'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { Languages } from 'lucide-react';
import { locales as allLocales, type Locale } from '@/lib/i18n/config';
import { localizedHref, stripLocale } from '@/lib/i18n/href';
import { trackLanguageSwitch } from '@/lib/analytics';

const LABEL: Record<Locale, string> = { en: 'EN', ar: 'AR', es: 'ES', ru: 'RU', fr: 'FR' };
// Label shown for the *other* language, in that language's own script —
// an English speaker doesn't need to know the Arabic word for Arabic, but
// showing "عربي" next to "EN" is the immediately recognizable convention
// used by most bilingual sites (and reads correctly regardless of the
// current page's own dir).
const NATIVE_LABEL: Record<Locale, string> = { en: 'English', ar: 'العربية', es: 'Español', ru: 'Русский', fr: 'Français' };

interface LanguageSwitcherProps {
  locale: Locale;
  // Locales to offer in the dropdown. Defaults to every structural locale
  // for any caller that doesn't pass one; Header.tsx passes the
  // runtime-enabled subset (see lib/i18n/config.ts's getEnabledLocales) so
  // a toggled-off language never appears as an option here in the first
  // place — not just redirected away if picked.
  locales?: Locale[];
  className?: string;
}

export default function LanguageSwitcher({ locale, locales = allLocales as unknown as Locale[], className = '' }: LanguageSwitcherProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const neutralPath = stripLocale(pathname, allLocales);

  // Click/tap outside closes the dropdown, and Escape closes it from the
  // keyboard — needed now that opening is a real toggle instead of a CSS
  // :hover state that used to close itself the moment the pointer left.
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-sm font-medium text-textSecondary hover:text-foreground transition-colors px-2 py-1.5 rounded-lg hover:bg-bg2"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Languages className="h-4 w-4" />
        {LABEL[locale]}
      </button>

      {/* Click/tap-toggled dropdown. A plain CSS :hover/:focus-within toggle
          doesn't work reliably on touch devices — there's no :hover at all,
          and iOS Safari specifically doesn't give a tapped <button> :focus
          either — so this needs real JS state to work on mobile, which is
          most of the traffic for an `ar` locale. */}
      <div
        className={`absolute end-0 top-full mt-1 min-w-[140px] bg-bg2 border border-borderDark rounded-xl shadow-card transition-[opacity,visibility] duration-150 z-50 py-1 ${
          open ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        {locales.map((l) => {
          const href = search ? `${localizedHref(l, neutralPath)}?${search}` : localizedHref(l, neutralPath);
          const active = l === locale;
          return (
            <Link
              key={l}
              href={href}
              onClick={() => {
                // Only fire for an actual switch — clicking the already-active
                // locale (harmless, just closes the dropdown) shouldn't count
                // as a language change.
                if (l !== locale) trackLanguageSwitch(locale, l);
                setOpen(false);
              }}
              className={`block px-3.5 py-2 text-sm transition-colors ${
                active
                  ? 'text-foreground font-semibold'
                  : 'text-textSecondary hover:text-foreground hover:bg-bg'
              }`}
              aria-current={active ? 'true' : undefined}
            >
              {NATIVE_LABEL[l]}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
